import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import { Contact } from "./FieldComponents";
import { CreateContact } from "./CreateContact";
import '../styles/Leads.css';
import { Text, NumberInput, DateInput, Dropdown, Boolean } from "./FieldComponents";
import { ChevronDown, Feather } from "lucide-react";
import Alert from "./Alert";

export const fetchUsers = async () => {
    try {
        const response = await fetch(`https://api.casedb.co/user.php?users=true&time=${new Date().getTime()}`, { credentials: 'include' });
        const data = await response.json();
        return data.users;
    } catch (error) {
        console.error("Error fetching users:", error);
    }
};

export const CreateLead = ({ user, setCreateLead, onLeadCreated }) => {
    const [createContact, setCreateContact] = useState(false);
    const [contactFieldType, setContactFieldType] = useState('main'); // 'main', 'referral', 'db_origination'
    const [selectedContact, setSelectedContact] = useState(null);
    const [referralContact, setReferralContact] = useState(null);
    const [dbOrigination, setDbOrigination] = useState(null);
    const [caseTypes, setCaseTypes] = useState([]);
    const [marketingSources, setMarketingSources] = useState([]);
    const [caseFields, setCaseFields] = useState([]);
    const [formData, setFormData] = useState({
        intake_by: user?.user_id || 12,
        contact_id: null,
        preferred_contact: '',
        case_type: '',
        incident_date: '',
        marketing_source: '',
        referral_contact: null,
        db_origination: null,
        marketing_notes: '',
        summary: '',
        case_likelihood: '',
        office: '',
        assigned_to: user?.user_id || 12,
        status: '',
        notes: '',
        custom_fields: {}
    });
    const [customFields, setCustomFields] = useState({
        222: `${new Date().getFullYear()}-${String(new Date().getMonth()).padStart(2, 0)}-${String(new Date().getDate()).padStart(2, 0)}`,
        223: String(new Date().toLocaleTimeString()).slice(0, 8),
        224: formData.intake_by,
        225: formData.marketing_source,
        226: formData.referral_contact,
        227: formData.db_origination
    });
    const [filteredFields, setFilteredFields] = useState([]);
    const [users, setUsers] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [message, setMessage] = useState(null);

    const saveFields = async (leadId) => {
        const field_values = {};
    
        for (const [fieldId, value] of Object.entries(customFields)) {
            field_values[fieldId] = typeof value === 'object' ? JSON.stringify(value) : value;
        }
    
        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php`, {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify({
                    lead_id: leadId,
                    field_values,
                }),
            });
    
            const result = await response.json();
            if (!result.success) {
                console.error("Error saving custom fields:", result.message);
            }
        } catch (error) {
            console.error("Error posting custom fields:", error);
        }
    };

    const fetchMarketingSources = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/marketing_sources.php?time=${new Date().getTime()}`, {
                credentials: 'include'
            });
            const data = await response.json();
            setMarketingSources(data.marketing_sources);
        } catch (error) {
            console.error("Error fetching marketing sources:", error);
        }
    };

    const fetchStatuses = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/statuses.php?time=${new Date().getTime()}`);
            const data = await response.json();
            setStatuses(data.statuses?.filter(status => status?.substatus === null));
        } catch (error) {
            console.error("Error fetching statuses:", error);
        }
    };

    const handleCaseTypeChange = (e) => {
        const caseTypeId = e.target.value;
        setFormData((prev) => ({ ...prev, case_type: caseTypeId }));
        fetchCaseFields(caseTypeId);
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({
            ...prev,
            custom_fields: {
                ...prev.custom_fields,
                [fieldId]: value,
            },
        }));
    };

    const handleNewContactCreated = (contact) => {
        setSelectedContact(contact);
        // Update form data with the new contact ID
        setFormData(prev => ({
            ...prev,
            contact_id: contact.id || contact.contact_id
        }));
        setCreateContact(false);
    };
    
    // Update formData.contact_id when selectedContact changes
    useEffect(() => {
        if (selectedContact?.id || selectedContact?.contact_id) {
            setFormData(prev => ({
                ...prev,
                contact_id: selectedContact.id || selectedContact.contact_id
            }));
        }
    }, [selectedContact]);
    
    useEffect(() => {
        if (formData.case_type) {
            fetchCaseFields(formData.case_type);
        }
    }, [formData.case_type]);

    const handleCreateLead = async () => {
        const customFields = { ...formData.custom_fields };
    
        const requestData = {
            ...formData,
            custom_fields: customFields,
            pfs: 0
        };
    
        try {
            const response = await fetch(`https://api.casedb.co/leads.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(requestData),
                credentials: 'include'
            });
    
            const data = await response.json();
    
            if (data.success) {
                setMessage({ message: 'Lead created successfully!', type: 'success' });
                setCreateLead(false);
                setFormData({
                    intake_by: user.user_id || 12,
                    contact_id: null,
                    preferred_contact: '',
                    case_type: '',
                    incident_date: '',
                    marketing_source: '',
                    referral_contact: null,
                    marketing_notes: '',
                    summary: '',
                    case_likelihood: '',
                    office: '',
                    assigned_to: user.user_id || 12,
                    status: '',
                    notes: '',
                    custom_fields: {}
                });
                await saveFields(data.lead_id);
                
                // Call onLeadCreated if provided
                if (typeof onLeadCreated === 'function') {
                    onLeadCreated(data.lead_id);
                }
            } else {
                console.error("Error creating lead:", data.message);
                setMessage({ message: data.message, type: 'error' });
            }
        } catch (error) {
            console.error("Error creating lead:", error);
            setMessage({ message: 'Error creating lead', type: 'error' });
        }
    };

    useEffect(() => {
        setCustomFields((prev) => ({
            ...prev,
            225: formData.marketing_source,
            226: formData.referral_contact,
            227: formData.db_origination
        }));
    }, [formData]);

    const fetchCaseTypes = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            setCaseTypes(data.case_types);
        } catch (error) {
            console.error("Error fetching case types:", error);
        }
    };

    const fetchCaseFields = async (caseTypeId) => {
        try {
            const response = await fetch(`https://api.casedb.co/case_fields.php?case_type_id=${caseTypeId}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setCaseFields(data.fields);
            } else {
                setCaseFields([]);
            }
        } catch (error) {
            console.error("Error fetching case fields:", error);
            setCaseFields([]);
        }
    };

    useEffect(() => {
        const updatedFields = caseFields.filter((field) => {
            const sectionIds = (() => {
                try {
                    return Array.isArray(field.section_id)
                        ? field.section_id
                        : JSON.parse(field.section_id);
                } catch {
                    return [];
                }
            })();
        
            if (sectionIds.includes(47)) return false;
            if (field.add_item === 1) return false;
        
            if (!field.display_when && !field.display_multi) return true;
        
            // Parse display_when/display_multi (check display_multi first)
            let parsedDisplayWhen = null;
            try {
                const toparse = field.display_multi || field.display_when;
                parsedDisplayWhen = JSON.parse(String(toparse));
            } catch (e) {
                parsedDisplayWhen = field.display_multi || field.display_when;
            }
            
            // Get first field from array or single value
            const fieldIdToCheck = Array.isArray(parsedDisplayWhen) ? parsedDisplayWhen[0] : parsedDisplayWhen;
            
            const dependentField = caseFields.find(f => f.id === fieldIdToCheck);
            if (!dependentField) return true;
        
            const dependentValue = formData.custom_fields[dependentField.id] ?? null;
            const expectedIndex = field.is_answered;
        
            const parsedOptions = (() => {
                try {
                    return Array.isArray(dependentField.options)
                        ? dependentField.options
                        : dependentField.options
                        ? JSON.parse(dependentField.options)
                        : [];
                } catch (error) {
                    console.error("Error parsing options:", dependentField.options, error);
                    return [];
                }
            })();
        
            const selectedIndex = parsedOptions.indexOf(dependentValue);
        
            return (expectedIndex === null && selectedIndex > -1) || Number(selectedIndex) === Number(expectedIndex);
        });

        setFilteredFields(updatedFields);
    }, [caseFields, formData]);

    useEffect(() => {
        fetchCaseTypes();
        fetchMarketingSources();
        fetchStatuses();

        const getUsers = async () => {
            const userList = await fetchUsers();
            if (userList) setUsers(userList);
        };

        getUsers();
    }, []);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            contact_id: selectedContact?.id || null
        }));
    }, [selectedContact]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            referral_contact: referralContact?.id || null
        }));
    }, [referralContact]);

    useEffect(() => {
        setFormData((prev) => ({
            ...prev,
            db_origination: dbOrigination?.id || null
        }));
    }, [dbOrigination]);

    useEffect(() => {
        setFormData({
            ...formData,
            custom_fields: {}
        });
    }, [formData.case_type]);


    return (
        <>
            <Modal 
                onClose={() => setCreateLead(false)}
                title="Create New Lead"
                header={(
                    <div className='modal-header-actions'>
                        <button className='action alt' onClick={() => setCreateLead(false)}>Cancel</button>
                        <button className='action' onClick={handleCreateLead}>Save</button>
                    </div>
                )}
                wide
            >
                <div className='modal-content-wrapper'>
                    <div className='sub-title'>
                        <h4>Intake</h4>
                    </div>
                    <div className='intake-details'>
                        <div className='form-group'>
                            <label htmlFor='intake_by' className='subtext'>Intake By</label>
                            <input type='text' className='subtext' id='intake_by' name='intake_by' value={user?.user ?? user?.name} disabled />
                        </div>
                        <div className='form-group'>
                            <label htmlFor='created_at' className='subtext'>Intake Date</label>
                            <input type='date' className='subtext' disabled value={new Date().toISOString().split('T')[0]} id='created_at' />
                        </div>
                    </div>
                    <div className='sub-title'>
                        <h4>Intake Details</h4>
                    </div>
                    <div className='lead-setup'>
                        <div className='form-group'>
                            <label htmlFor='contact_id' className='subtext'>Contact<span className='required'>*</span></label>
                            <Contact 
                                selectedContact={selectedContact}
                                setSelectedContact={setSelectedContact}
                                onChange={(e) => setFormData({ ...formData, contact_id: e.target.value })}
                                onCreateNewContact={() => setCreateContact(true)}
                                name='contact_id'
                            />
                        </div>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='preferred-contact' className='subtext'>Preferred Contact Method</label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' id='preferred-contact' value={formData.preferred_contact} onChange={(e) => setFormData({ ...formData, preferred_contact: e.target.value })}>
                                    <option value=''>Select...</option>
                                    <option value='email'>Email</option>
                                    <option value='phone'>Phone</option>
                                    <option value='text'>Text</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    </div>
                    <div className='case-specs'>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='case-type-intake' className='subtext'>Case Type<span className="required">*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} required id='case-type-intake' value={formData.case_type} onChange={(e) => {setFormData({ ...formData, case_type: e.target.value }); handleCaseTypeChange(e);}}>
                                    <option value=''>Select...</option>
                                    {caseTypes.filter(type => Number(type.id) !== 0).map((type) => (
                                        <option key={type.id} value={type.id}>{type.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='incident-date' className='subtext'>Incident Date</label>
                            <input id='incident-date' type='date' value={formData.incident_date} onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })} />
                        </div>
                    </div>
                    <div className='sub-title'>
                        <h4>Marketing Information</h4>
                    </div>
                    <div className='marketing-info'>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='intake-source' className='subtext'>Marketing Source<span className="required">*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} value={formData.marketing_source} onChange={(e) => setFormData({ ...formData, marketing_source: e.target.value })}>
                                    <option value=''>Select...</option>
                                    {marketingSources.map((source) => (
                                        <option key={source.id} value={source.id}>{source.source_name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        {marketingSources.some(source => source.id == formData.marketing_source && source.marketing_type === '3' && formData.marketing_source != '12') && (
                            <div className='form-group'>
                                <label className='subtext'>Referral Source</label>
                                <Contact 
                                    selectedContact={referralContact}
                                    setSelectedContact={setReferralContact}
                                    onCreateNewContact={() => setCreateContact(true)}
                                />
                            </div>
                        )}
                        {Number(formData.marketing_source) >= 11 && Number(formData.marketing_source) <= 19 && (
                            <div className='form-group'>
                                <label className='subtext'>DB Origination</label>
                                <Contact 
                                    selectedContact={dbOrigination}
                                    setSelectedContact={setDbOrigination}
                                    onCreateNewContact={() => setCreateContact(true)}
                                />
                            </div>
                        )}
                        <div className='form-group'>
                            <label htmlFor='marketing_notes' className='subtext'>Marketing Notes</label>
                            <textarea id='marketing_notes' placeholder='Marketing Notes...' value={formData.marketing_notes} onChange={(e) => setFormData({ ...formData, marketing_notes: e.target.value })}></textarea>
                        </div>
                    </div>
                    {/*filteredFields.length > 0 && <div className='sub-title'>
                        <h4>Case Details</h4>
                    </div>}
                    <div className='case-details'>
                        {filteredFields.map((field) => {
                            if (field.hidden === 2) return null;

                            switch (field.field_id) {
                                case 1:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <Text
                                                type="text"
                                                placeholder={field.name}
                                                value={formData.custom_fields[field.id] ?? ""}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                case 2:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <Text
                                                type="textarea"
                                                placeholder={field.name}
                                                value={formData.custom_fields[field.id] ?? ""}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                case 3:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <NumberInput
                                                type="currency"
                                                value={formData.custom_fields[field.id] ?? ""}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                case 6:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <DateInput
                                                value={formData.custom_fields[field.id] ?? ""}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                case 8:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label htmlFor={field.id} className='subtext'>{field.name}</label>
                                            <Contact
                                                selectedContact={formData.custom_fields[field.id]}
                                                setSelectedContact={(contact) => handleFieldChange(field.id, contact)}
                                                onCreateNewContact={() => setCreateContact(true)}
                                            />
                                        </div>
                                    );
                                case 10:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <Dropdown
                                                options={field.options ?? "[]"}
                                                value={formData.custom_fields[field.id] ?? ""}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                case 12:
                                    return (
                                        <div className='form-group' key={field.id}>
                                            <label className='subtext'>{field.name}</label>
                                            <Boolean
                                                options={field.options ?? []}
                                                value={formData.custom_fields[field.id] ?? 2}
                                                onChange={(value) => handleFieldChange(field.id, value)}
                                            />
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        })}
                    </div>*/}
                    <div className='sub-title'>
                        <h4>Office Details</h4>
                    </div>
                    <div className='office-details'>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='likelihood' className='subtext'>Case Likelihood<span className="required">*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} value={formData.case_likelihood} onChange={(e) => setFormData({ ...formData, case_likelihood: e.target.value })}>
                                    <option value='' disabled>Select...</option>
                                    <option value='1'>No Case</option>
                                    <option value='2'>Unlikely Case</option>
                                    <option value='3'>Possible Case</option>
                                    <option value='4'>Likely Case</option>
                                    <option value='5'>Very Likely Case</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='office' className='subtext'>Office<span className="required">*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} value={formData.office} onChange={(e) => setFormData({ ...formData, office: e.target.value })}>
                                    <option value='' disabled>Select...</option>
                                    <option value='1'>Texas</option>
                                    <option value='2'>Colorado</option>
                                    <option value='3'>Louisiana</option>
                                    <option value='4'>Florida</option>
                                    <option value='5'>Oklahoma</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='assigned-to' className='subtext'>Assigned To<span className='required'>*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} value={formData.assigned_to} onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}>
                                    <option value='' disabled>Select...</option>
                                    {users?.map((user) => (
                                        <option key={user.id} value={user.id}>{user.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className='form-group filter-group' style={{ width: '100%' }}>
                            <label htmlFor='status' className='subtext'>Status<span className='required'>*</span></label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select className='filter-select' style={{ width: '100%' }} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                                    <option value='' disabled>Select...</option>
                                    {statuses.map((status) => (
                                        <option key={status.id} value={status.id}>{status.name}</option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                    </div>
                </div>
            </Modal>
            {createContact && <CreateContact setCreateContact={setCreateContact} onContactCreated={handleNewContactCreated} />}
            {message && <Alert message={message.message} type={message.type} onClose={() => setMessage(null)} />}
        </>
    );
};
